// @ts-nocheck
import React, { useContext, useState, useEffect } from "react";
import {
  LeftThresholdText,
  RightThresholdText,
  StyledDash,
  ThresholdLineGrid
} from "./Thresholds.styles";
import { Stack, TextField } from "@mui/material";
import { GlobalContext } from "context/Provider";

interface LineProps {
  type: string;
  alertLevel: "moderate" | "high";
  units: string;
  isGlobal: boolean;
  isDisabled?: boolean;
  onChangeFunc?: any;
}

export default function ThresholdLine(props: LineProps) {
  const { type, alertLevel, units, isGlobal, isDisabled, onChangeFunc } = props;
  const { threshold, thresholdDispatch, config } = useContext(GlobalContext);

  const visibility = config?.spirometryVisibility || {
    fev1: true,
    fvc: true,
    fev1fvc: true,
    pef: true,
  };

  const visibilityMap: Record<string, boolean> = {
    FEV1: visibility.fev1,
    FVC: visibility.fvc,
    FEV1_FVC: visibility.fev1fvc,
    PEF: visibility.pef,
  };

  const typeCast = (type: string) => {
    switch (type) {
      case "Oxygen Saturation":
        return "spo2";
      case "Temperature":
        return "temp";
      case "Activity":
        return "steps";
      case "FEV1":
      case "FVC":
      case "FEV1_FVC":
      case "PEF":
        return type;
      default:
        return type.toLowerCase();
    }
  };
  const key = typeCast(type);
  const [localLow, setLocalLow] = useState(threshold[alertLevel]?.[key]?.low ?? "");
  const [localHigh, setLocalHigh] = useState(threshold[alertLevel]?.[key]?.high ?? "");

  useEffect(() => {
    setLocalLow(threshold[alertLevel]?.[key]?.low ?? "");
    setLocalHigh(threshold[alertLevel]?.[key]?.high ?? "");
  }, [threshold, alertLevel, key]);

  const displayLabel = (type: string) => {
    if (type === "FEV1_FVC") return "FEV1/FVC";
    return type;
  };

  const isSpirometry = ["FEV1", "FVC", "FEV1_FVC", "PEF"].includes(key);
  if (!visibilityMap[key] && isSpirometry) {
    return null;
  }

  return (
    <ThresholdLineGrid container>
      <Stack direction="row" spacing={0.5} alignItems={"center"}>
        {!isGlobal && ( <LeftThresholdText>{displayLabel(type)}</LeftThresholdText> )}
        <TextField
          name={`${alertLevel}_${type.replace(" ", "_")}_low`}
          type="number"
          value={localLow}
          disabled={isDisabled}
          size="small"
          onFocus={() => {
            setLocalLow("");
          }}
          onChange={(e) => {
            let newValue = e.target.value;
            if(newValue.startsWith(".")) newValue = "0" + newValue;
            setLocalLow(newValue);

            thresholdDispatch({
              type: `${alertLevel}_${key}_low`,
              payload: Number(newValue)
            });

            onChangeFunc?.();
          }}
          onBlur={() => {
            const original = threshold[alertLevel][typeCast(type)]?.low ?? "";
            if (localLow === "") setLocalLow(original);
          }}
          sx={{
            '& .MuiInputBase-input.Mui-disabled': {
              backgroundColor: '#f5f5f5',
              WebkitTextFillColor: '#00000099',
            }
          }}
        />
        <StyledDash />
        <TextField
          name={`${alertLevel}_${type.replace(" ", "_")}_high`}
          type="number"
          value={localHigh}
          disabled={isDisabled}
          size="small"
          onFocus={() => {
            setLocalHigh("");
          }}
          onChange={(e) => {
            let newValue = e.target.value;
            if(newValue.startsWith(".")) newValue = "0" + newValue;
            setLocalHigh(newValue);

            thresholdDispatch({
              type: `${alertLevel}_${key}_high`,
              payload: Number(newValue)
            });

            onChangeFunc?.();
          }}
          onBlur={() => {
            const original = threshold[alertLevel][typeCast(type)]?.high ?? "";
            if (localHigh === "") setLocalHigh(original);
          }}
          sx={{
            '& .MuiInputBase-input.Mui-disabled': {
              backgroundColor: '#f5f5f5',
              WebkitTextFillColor: '#00000099',
            }
          }}
        />
        <RightThresholdText>{units}</RightThresholdText>
      </Stack>
    </ThresholdLineGrid>
  );
}
